import { MouseEvent, useCallback, useState } from 'react';

import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import { DatatableWrapper, TableBody, TableColumnType, TableHeader } from 'react-bs-datatable';
import { Button, Stack, Table } from 'react-bootstrap';

import { DraftOrderNameCell } from '@components/DraftOrder/DraftOrderNameCell';
import InfoIconButton from '@components/Button/InfoIconButton';
import ExpandableSection from '@components/ExpandableSection/ExpandableSection';
import InfoDialog from '@components/Modals/InfoDialog';
import { Sanitize } from '@components/Sanitize';
import { Clinician } from '@customTypes/clinician';
import { IDraftOrder } from '@customTypes/draftOrder';
import { getClinicianName } from '@helpers/clinician';
import { ROUTE_PATHS, getPatientDetailsPath } from '@router/paths';

import NoDraftsContent from '../NoDraftsContent/NoDraftsContent';

interface IProps {
  completedOrders: IDraftOrder[];
  loading: boolean;
}

export default function CompletedDrafts({ completedOrders, loading }: IProps) {
  const navigate = useNavigate();

  const [showInfoDialog, setShowInfoDialog] = useState<boolean>(false);

  const onOrderIDAction = useCallback(
    (evt: MouseEvent, draftOrder: IDraftOrder) => {
      evt.preventDefault();
      const patientDetailsPath = getPatientDetailsPath(draftOrder.created_requisition_patient_identifier!);

      navigate(patientDetailsPath, {
        state: { prevPath: ROUTE_PATHS.DRAFT_REQUISITION_LIST },
      });
    },
    [navigate],
  );

  const headers: TableColumnType<IDraftOrder>[] = [
    {
      title: 'Order ID',
      prop: 'created_requisition',
      isSortable: true,
      alignment: { horizontal: 'left' },
      cell: row => (
        <Button
          as='a'
          variant='link'
          size='sm'
          className='fs-6 ps-0 text-decoration-none'
          onClick={evt => onOrderIDAction(evt, row)}
        >
          {row.created_requisition}
        </Button>
      ),
    },
    {
      title: 'Draft Name',
      prop: 'name',
      isSortable: true,
      alignment: { horizontal: 'left' },
      cell: row => (
        <DraftOrderNameCell draft={row}>
          <Sanitize>{row.name}</Sanitize>
        </DraftOrderNameCell>
      ),
    },
    {
      title: 'Ordered By',
      prop: 'created_requisition_by',
      cell: (row: IDraftOrder) => getClinicianName(row.created_requisition_by),
      isSortable: true,
      alignment: { horizontal: 'left' },
    },
    {
      title: 'Ordering Provider',
      prop: 'ordering_provider',
      cell: (row: IDraftOrder) => getClinicianName(row.ordering_provider),
      isSortable: true,
      alignment: { horizontal: 'left' },
    },
    {
      title: 'Created By',
      prop: 'created_by',
      cell: (row: IDraftOrder) => getClinicianName(row.created_by),
      isSortable: true,
      alignment: { horizontal: 'left' },
    },
    {
      title: 'Ordered On',
      prop: 'created_requisition_at',
      cell: row => dayjs.utc(row.created_requisition_at).local().format('MM/DD/YYYY'),
      isSortable: true,
      alignment: { horizontal: 'left' },
    },
  ];

  return (
    <>
      <ExpandableSection
        id='completedDrafts'
        expandableContentId='completedDraftsContent'
        headerTitle={
          <>
            <span>Completed Drafts ({completedOrders.length})</span>
            <InfoIconButton onClick={() => setShowInfoDialog(true)} aria-label='Completed Drafts Info Button' />
          </>
        }
        defaultIsCollapsed
        loading={loading}
      >
        {completedOrders.length === 0 ? (
          <NoDraftsContent content={'You have no completed draft orders'} />
        ) : (
          <DatatableWrapper
            body={completedOrders}
            headers={headers}
            sortProps={{
              initialState: { prop: 'created_requisition_at', order: 'desc' },
              columnValueProcessor: {
                name: (value: string) => value.toLowerCase(),
                ordering_provider: (clinician: Clinician) => getClinicianName(clinician),
                created_by: (clinician?: Clinician) => getClinicianName(clinician),
                created_requisition_by: (clinician?: Clinician) => getClinicianName(clinician),
              },
            }}
          >
            <Table className='table-with-sticky-header' responsive>
              <TableHeader />
              <TableBody />
            </Table>
          </DatatableWrapper>
        )}
      </ExpandableSection>
      {showInfoDialog && (
        <InfoDialog onClose={() => setShowInfoDialog(false)} title='Completed Drafts'>
          <Stack gap={4}>
            <p className='mb-0'>
              Drafts that were submitted as orders will appear here. Each completed draft will have an order ID that
              links to the patient’s page for the order.
            </p>
            <p className='mb-0'>Drafts completed more than 30 days ago are not listed.</p>
            <p className='mb-0'>
              Drafts labeled <strong>Reanalysis</strong> were submitted as reanalysis orders from an existing
              requisition.
            </p>
          </Stack>
        </InfoDialog>
      )}
    </>
  );
}
